import React, { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { usePostHog } from "posthog-js/react";

/**
 * CtaCard Component
 *
 * A call-to-action card component with customizable title, subtitle, description, and button.
 *
 * @param {Object} props
 * @param {string} [props.title="CTA Title"] - The main title of the card
 * @param {string} [props.subtitle="CTA Subtitle"] - The subtitle displayed in a highlighted box
 * @param {string} [props.description="CTA Description"] - The description text of the card
 * @param {string} [props.ctaLabel="CTA"] - The label text for the call-to-action button
 * @param {string} [props.ctaLink="#"] - The URL or route the button links to
 * @returns {React.ReactElement} A call-to-action card component
 */
export default function CtaCard({
  title = "CTA Title",
  subtitle = "CTA Subtitle",
  description = "CTA Description",
  ctaLabel = "CTA",
  ctaLink = "#",
  ctaTrack = "cta-click",
  image = null,
}) {
  const linkWrapperRef = useRef(null);
  const [bottomMargin, setBottomMargin] = useState(0);
  const posthog = usePostHog();

  const handleClick = () => {
    posthog.capture(ctaTrack);
  };

  useEffect(() => {
    if (image) return;
    if (linkWrapperRef.current) {
      // Get the height of the link wrapper and add some padding
      const height = linkWrapperRef.current.offsetHeight;
      setBottomMargin(height + 20); // Adding extra 12px for spacing
    }

    // Set up resize observer to handle responsive changes
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0] && linkWrapperRef.current) {
        const height = linkWrapperRef.current.offsetHeight;
        setBottomMargin(height + 20);
      }
    });

    if (linkWrapperRef.current) {
      resizeObserver.observe(linkWrapperRef.current);
    }

    return () => {
      if (linkWrapperRef.current) {
        resizeObserver.unobserve(linkWrapperRef.current);
      }
    };
  }, []);

  return (
    <div className="relative min-h-42 md:min-h-auto mt-3 w-full max-w-xl bg-violet-cyt rounded-xl shadow-md border border-gray-200">
      <div
        className="relative p-6 space-y-4"
        id="cta-content"
        style={{ paddingBottom: `${bottomMargin}px` }}
      >
        {title && <h3 className="text-2xl font-semibold">{title}</h3>}
        {subtitle && (
          <span className="text-lg font-medium bg-white px-2 py-1 rounded-lg">
            {subtitle}
          </span>
        )}
        {description && <p className="mt-2 text-sm">{description}</p>}
      </div>

      <div className="w-full flex flex-row-reverse">
        {image && <img src={image} alt={title} className="w-2/3" />}
      </div>

      <div
        className="absolute bottom-3 px-6 w-full"
        id="link-wrapper"
        ref={linkWrapperRef}
      >
        <Link to={ctaLink} className="w-full ">
          <Button className="w-full cursor-pointer" onClick={handleClick}>
            {ctaLabel}
          </Button>
        </Link>
      </div>
    </div>
  );
}

CtaCard.propTypes = {
  /** The main title of the card */
  title: PropTypes.string,
  /** The subtitle displayed in a highlighted box */
  subtitle: PropTypes.string,
  /** The description text of the card */
  description: PropTypes.string,
  /** The label text for the call-to-action button */
  ctaLabel: PropTypes.string,
  /** The URL or route the button links to */
  ctaLink: PropTypes.string,
  /** The event name for tracking CTA clicks */
  ctaTrack: PropTypes.string,
};

CtaCard.defaultProps = {
  title: "CTA Title",
  subtitle: "CTA Subtitle",
  description: "CTA Description",
  ctaLabel: "CTA",
  ctaLink: "#",
  ctaTrack: "cta-click",
};
